import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  Timestamp,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';

const COLLECTION_NAME = 'employee-payroll-timesheets';
const ROWS_COLLECTION_NAME = 'employee-payroll-timesheet-rows';

export interface ExternalPayrollItem {
  maNV: string;
  tenNhanVien: string;
  totalHours: number;
}

export interface PayrollDayDetail {
  ngay: string;
  maNV: string;
  tenNhanVien: string;
  totalHours: number;
}

const normalizeText = (value: string): string => {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '')
    .toLowerCase();
};

const sanitizeIdPart = (value: string): string => {
  return value.replace(/[\\/#?\[\]]/g, '_').replace(/\s+/g, '_');
};

const makeRowUniqueKey = (row: PayrollDayDetail): string => {
  const dayKey = sanitizeIdPart(row.ngay || 'unknown-day');
  const employeeKey = row.maNV ? sanitizeIdPart(row.maNV) : sanitizeIdPart(normalizeText(row.tenNhanVien || 'unknown'));
  return `${dayKey}-${employeeKey}`;
};

export interface PayrollTimesheetImport {
  monthKey: string;
  monthLabel: string;
  fileName: string;
  hoursByUserId: Record<string, number>;
  externalItems: ExternalPayrollItem[];
  unmatchedRows: string[];
  wageOverrides?: Record<string, number>;
  rowDetailsByWageKey?: Record<string, PayrollDayDetail[]>;
  uploadedAt: Date;
  updatedAt: Date;
}

const retryOperation = async <T>(operation: () => Promise<T>, maxRetries = 3): Promise<T> => {
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      if (attempt === maxRetries) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * 350));
    }
  }
  throw new Error('Max retries exceeded');
};

const toDate = (value: unknown): Date => {
  if (value instanceof Date) {
    return value;
  }

  if (value instanceof Timestamp) {
    return value.toDate();
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return new Date(value);
  }

  return new Date();
};

const mapImportDoc = (data: Record<string, unknown>, fallbackMonthKey: string): PayrollTimesheetImport => {
  return {
    monthKey: typeof data.monthKey === 'string' ? data.monthKey : fallbackMonthKey,
    monthLabel: typeof data.monthLabel === 'string' ? data.monthLabel : fallbackMonthKey,
    fileName: typeof data.fileName === 'string' ? data.fileName : 'unknown.xlsx',
    hoursByUserId: typeof data.hoursByUserId === 'object' && data.hoursByUserId !== null
      ? (data.hoursByUserId as Record<string, number>)
      : {},
    externalItems: Array.isArray(data.externalItems)
      ? (data.externalItems as ExternalPayrollItem[])
      : [],
    unmatchedRows: Array.isArray(data.unmatchedRows)
      ? data.unmatchedRows.filter((item): item is string => typeof item === 'string')
      : [],
    wageOverrides:
      typeof data.wageOverrides === 'object' && data.wageOverrides !== null
        ? (data.wageOverrides as Record<string, number>)
        : {},
    rowDetailsByWageKey:
      typeof data.rowDetailsByWageKey === 'object' && data.rowDetailsByWageKey !== null
        ? (data.rowDetailsByWageKey as Record<string, PayrollDayDetail[]>)
        : {},
    uploadedAt: toDate(data.uploadedAt),
    updatedAt: toDate(data.updatedAt),
  };
};

export const payrollTimesheetService = {
  async saveMonthlyImport(payload: {
    monthKey: string;
    monthLabel: string;
    fileName: string;
    hoursByUserId: Record<string, number>;
    externalItems: ExternalPayrollItem[];
    unmatchedRows: string[];
    wageOverrides?: Record<string, number>;
    rowDetailsByWageKey?: Record<string, PayrollDayDetail[]>;
  }): Promise<void> {
    return retryOperation(async () => {
      const docRef = doc(db, COLLECTION_NAME, payload.monthKey);
      const now = new Date();
      const existing = await getDoc(docRef);

      await setDoc(
        docRef,
        {
          ...payload,
          uploadedAt: existing.exists() ? existing.data().uploadedAt ?? now : now,
          updatedAt: now,
        },
        { merge: true }
      );
    });
  },

  async getMonthlyImport(monthKey: string): Promise<PayrollTimesheetImport | null> {
    return retryOperation(async () => {
      const snapshot = await getDoc(doc(db, COLLECTION_NAME, monthKey));
      if (!snapshot.exists()) {
        return null;
      }

      return mapImportDoc(snapshot.data() as Record<string, unknown>, monthKey);
    });
  },

  async getAllMonthlyImports(): Promise<PayrollTimesheetImport[]> {
    return retryOperation(async () => {
      const importsQuery = query(collection(db, COLLECTION_NAME), orderBy('monthKey', 'desc'));
      const snapshot = await getDocs(importsQuery);
      return snapshot.docs.map((item) => mapImportDoc(item.data() as Record<string, unknown>, item.id));
    });
  },

  async updateMonthlyWageOverrides(monthKey: string, wageOverrides: Record<string, number>): Promise<void> {
    return retryOperation(async () => {
      const docRef = doc(db, COLLECTION_NAME, monthKey);
      await setDoc(
        docRef,
        {
          wageOverrides,
          updatedAt: new Date(),
        },
        { merge: true }
      );
    });
  },

  async saveMonthlyRows(monthKey: string, fileName: string, rows: PayrollDayDetail[]): Promise<void> {
    return retryOperation(async () => {
      const existingRowsQuery = query(collection(db, ROWS_COLLECTION_NAME), where('monthKey', '==', monthKey));
      const existingSnapshot = await getDocs(existingRowsQuery);

      if (!existingSnapshot.empty) {
        await Promise.all(existingSnapshot.docs.map((item) => deleteDoc(item.ref)));
      }

      const dedupedMap = new Map<string, PayrollDayDetail>();
      rows.forEach((row) => {
        const uniqueKey = makeRowUniqueKey(row);
        const existingRow = dedupedMap.get(uniqueKey);
        if (!existingRow) {
          dedupedMap.set(uniqueKey, row);
          return;
        }

        if ((row.totalHours ?? 0) > (existingRow.totalHours ?? 0)) {
          dedupedMap.set(uniqueKey, row);
        }
      });

      const now = new Date();
      const batch = writeBatch(db);
      Array.from(dedupedMap.values()).forEach((row) => {
        const rowId = `${monthKey}-${makeRowUniqueKey(row)}`;
        const rowRef = doc(db, ROWS_COLLECTION_NAME, rowId);
        batch.set(rowRef, {
          monthKey,
          fileName,
          ngay: row.ngay,
          maNV: row.maNV,
          tenNhanVien: row.tenNhanVien,
          totalHours: row.totalHours,
          createdAt: now,
        });
      });

      await batch.commit();
    });
  },

  async getMonthlyRows(monthKey: string): Promise<PayrollDayDetail[]> {
    return retryOperation(async () => {
      const rowsQuery = query(collection(db, ROWS_COLLECTION_NAME), where('monthKey', '==', monthKey));
      const snapshot = await getDocs(rowsQuery);
      return snapshot.docs
        .map((item) => {
          const data = item.data();
          return {
            ngay: typeof data.ngay === 'string' ? data.ngay : '',
            maNV: typeof data.maNV === 'string' ? data.maNV : '',
            tenNhanVien: typeof data.tenNhanVien === 'string' ? data.tenNhanVien : '',
            totalHours: typeof data.totalHours === 'number' ? data.totalHours : 0,
          };
        })
        .sort((a, b) => a.ngay.localeCompare(b.ngay));
    });
  },
};
